// src/App.jsx
import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAppStore } from './stores/useAppStore';
import { getMe } from './services/integrations/atlassian';
import ProtectedRoute, { RoleRoute } from './components/protectedRoute';
import MainLayout from './components/layout/mainLayout';
import PageLoader from './components/ui/pageLoader';

// Code-split every route so the landing page doesn't pay for the full
// authenticated bundle on first paint.
const GoalsPage = lazy(() => import('./pages/Goals'));
const OneOnOnePage = lazy(() => import('./pages/OneOnOne'));
const IntegrationsPage = lazy(() => import('./pages/Integrations'));
const AdminPage = lazy(() => import('./pages/Admin'));
const LoginPage = lazy(() => import('./pages/Auth'));
const MemberProfilePage = lazy(() => import('./pages/MemberProfile'));
const FeedbackPage = lazy(() => import('./pages/Feedback'));
const FeedbackRespondPage = lazy(() => import('./pages/Feedback/Respond'));
const LandingPage = lazy(() => import('./pages/Landing'));
const TermsPage = lazy(() => import('./pages/Legal/terms'));
const PrivacyPolicyPage = lazy(() => import('./pages/Legal/privacyPolicy'));
const SupportPage = lazy(() => import('./pages/Legal/support'));
const EulaPage = lazy(() => import('./pages/Legal/eula'));
const SlackDocumentation = lazy(() => import('./pages/Docs/slack'));
const AtlassianDocumentation = lazy(() => import('./pages/Docs/atlassian'));
const DocsPage = lazy(() => import('./pages/Docs'));
const SecurityPage = lazy(() => import('./pages/Legal/security'));
const DpaPage = lazy(() => import('./pages/Legal/dpa'));
const SlaPage = lazy(() => import('./pages/Legal/sla'));
const AboutPage = lazy(() => import('./pages/Legal/about'));
const DecisionPage = lazy(() => import('./pages/Decisions'));
const SignalDashboardPage = lazy(() => import('./pages/SignalDashboard'));
const BlogPage = lazy(() => import('./pages/Blog'));
const NotFoundPage = lazy(() => import('./pages/NotFound'));


/** Redirects /app to the correct home page based on the user's role. */
function RoleIndexRedirect() {
  const currentUser = useAppStore(state => state.currentUser);
  if (currentUser?.isAdmin) return <Navigate to="/app/admin" replace />;
  if (currentUser?.isManager) return <Navigate to="/app/dashboards" replace />;
  return <Navigate to="/" replace />;
}

export default function App() {

  useEffect(() => {
    // 1. Hydrate from localStorage immediately so the UI doesn't flash a
    //    logged-out state while we wait on the network.
    useAppStore.getState().hydrateAuthFromStorage();

    // 2. Only probe /auth/me if localStorage says we *think* we're signed in.
    //    Calling it on a fresh visit to the landing page guarantees a 401,
    //    which the global axios interceptor turns into a hard redirect to
    //    `/`. Since we're already on `/`, that just remounts App and triggers
    //    another probe → 401 → redirect → loop, until the server starts
    //    returning 429. Skipping the probe when authenticated=false breaks
    //    the cycle at the source.
    const store = useAppStore.getState();
    if (!store.authenticated) return;

    // 3. Refresh the user from /auth/me in the background. Catches role
    //    changes (e.g. promoted to manager from inside the Forge admin panel)
    //    that aren't reflected in the cached localStorage snapshot.
    (async () => {
      try {
        const fresh = await getMe();
        if (!fresh) return;
        // Replace the cached user with the fresh copy. Preserves the existing
        // session token + org reference; only the user role/plan fields change.
        useAppStore.getState().setAuthSnapshot({
          token: store.authToken || '__session__',
          user: fresh,
          org: store.organization || (fresh.orgId ? { id: fresh.orgId } : null),
        });
      } catch {
        // /auth/me failing here means the cookie session expired even though
        // localStorage looked valid. The 401 interceptor will log us out and
        // redirect — nothing more for us to do here.
      }
    })();
  }, []);

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* ---------- Public ---------- */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/eula" element={<EulaPage />} />
      <Route path="/blog/:name" element={<BlogPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/docs/slack" element={<SlackDocumentation />} />
      <Route path="/docs/atlassian" element={<AtlassianDocumentation />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/dpa" element={<DpaPage />} />
      <Route path="/sla" element={<SlaPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route
        path="/feedback/forms/:token/respond"
        element={<FeedbackRespondPage />}
      />

      {/* ---------- Protected ---------- */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* Role-aware index redirect */}
        <Route index element={<RoleIndexRedirect />} />

        {/* Manager-only routes */}
        <Route path="dashboards" element={<RoleRoute roles={['manager']}><SignalDashboardPage /></RoleRoute>} />
        <Route path="one-on-ones" element={<RoleRoute roles={['manager']}><OneOnOnePage /></RoleRoute>} />
        <Route path="goals" element={<RoleRoute roles={['manager']}><GoalsPage /></RoleRoute>} />
        <Route path="feedback" element={<RoleRoute roles={['manager']}><FeedbackPage /></RoleRoute>} />
        <Route path="decisions" element={<RoleRoute roles={['manager']}><DecisionPage /></RoleRoute>} />
        <Route path="member/:id" element={<RoleRoute roles={['manager']}><MemberProfilePage /></RoleRoute>} />

        {/* Admin-only routes */}
        <Route path="admin" element={<RoleRoute roles={['admin']}><AdminPage /></RoleRoute>} />
        <Route path="integrations" element={<RoleRoute roles={['admin']}><IntegrationsPage /></RoleRoute>} />

        {/* Unknown /app/* routes */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* ---------- Fallback (public unknown routes) ---------- */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}
